import { defineStore } from 'pinia'
import {
  RestaurantAreas,
  RestaurantAreaType,
  SelectedLocationWithCoords,
  OpenServicesState,
} from '~/lib/interfaces/restaurant'
import {
  geoPermissionStatus,
  Restaurant,
  RestaurantDelivery,
  selectedRestaurantSchema,
  SelectRestaurantType,
} from '~/lib/services/store/restaurant/restaurant.dto'
import { loadGoogleAPI } from '~/lib/clients/googleMaps'
import { Address } from '~/lib/services/store/order/order.dto'
import { Coords } from '~/lib/models/geolocation'
import { DLVModalState } from '~/lib/models/dlv-modal'
import { TipsOptions } from '~/lib/services/store/common.dto'

interface State {
  restaurant?: Restaurant
  modalMOP: boolean
  modalDLV: boolean
  modalDLVState: DLVModalState | undefined
  geoPermissionStatus: geoPermissionStatus
  selectedGoogleLocation: SelectedLocationWithCoords | undefined
  modalChangeRestaurant: boolean
  candidateArea?: RestaurantAreaType
  provisionalAddress: Address | undefined // handles address from modal seach, favorite edition, before restaurant is selected
  selectedRestaurant: SelectRestaurantType
  googleMapsSessionToken: google.maps.places.AutocompleteSessionToken | undefined
  restaurantTipsOptions?: TipsOptions
  restaurantOpenState?: OpenServicesState
  productIdentifierAfterRestaurantSelected?: string
  productIdentifierNotFound?: boolean
}

export const useRestaurantStore = defineStore('restaurant', {
  state: (): State => ({
    restaurant: undefined,
    modalMOP: false,
    modalDLV: false,
    modalDLVState: undefined,
    geoPermissionStatus: 'prompt',
    selectedGoogleLocation: undefined,
    modalChangeRestaurant: false,
    candidateArea: undefined,
    provisionalAddress: undefined,
    selectedRestaurant: undefined,
    googleMapsSessionToken: undefined,
    restaurantTipsOptions: undefined,
    restaurantOpenState: undefined,
    productIdentifierAfterRestaurantSelected: undefined,
    productIdentifierNotFound: false,
  }),
  actions: {
    async getRestaurantByCode({ code, area }: { code: string; area: RestaurantAreaType }) {
      try {
        const { restaurantServiceRef } = useStoreService()
        const restaurantService = restaurantServiceRef()
        const restaurant = await restaurantService.getStoreRestaurantByCode({
          restaurantCode: code,
          area,
          countryCode: useCountry(),
        })
        this.restaurant = restaurant
      } catch (err) {
        throw new Error('Error fetching restaurant by code')
      }
    },
    async getRestaurant() {
      try {
        if (this.selectedRestaurant?.restaurant?.code) {
          const { restaurantServiceRef } = useStoreService()
          const restaurantService = restaurantServiceRef()
          const restaurant = await restaurantService.getStoreRestaurantByCode({
            restaurantCode: this.selectedRestaurant.restaurant.code,
            area: this.areaSelected || RestaurantAreas.MOP,
            countryCode: useCountry(),
          })

          this.setRestaurant(restaurant)
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log('🚀 ~ file: restaurant.store.ts:70 ~ getRestaurant ~ error:', error)
      }
    },
    setRestaurant(restaurant: Restaurant | RestaurantDelivery) {
      const selectedNewArea = this.getSelectedArea
      this.restaurant = restaurant

      const addressDLV =
        selectedNewArea === RestaurantAreas.MOP
          ? undefined
          : this.provisionalAddress ?? this.selectedRestaurant?.addressDLV

      this.selectedRestaurant = {
        ...this.selectedRestaurant,
        selectedArea: selectedNewArea,
        restaurant: {
          id: restaurant?.id,
          code: restaurant?.code,
          name: restaurant?.name,
          city: restaurant.city,
          address: restaurant?.address,
        },
        addressDLV,
        serviceFee: restaurant.serviceFee,
        smallOrderFee: restaurant.smallOrderFee,
      }
    },
    setIsCrew(isCrew: boolean) {
      this.selectedRestaurant = {
        ...this.selectedRestaurant,
        isCrew,
      }
    },
    setAddress(place: Address) {
      const { updateListByCoords } = useRestaurantsMOP()
      this.provisionalAddress = {
        ...place,
      }

      updateListByCoords({ lat: place.latitude, lng: place.longitude })
    },
    setGoogleLocation(place: SelectedLocationWithCoords) {
      this.selectedGoogleLocation = place
    },
    setGeoPermissionStatus(status: geoPermissionStatus) {
      this.geoPermissionStatus = status
    },
    updateAddress(obj: {
      number: string | undefined
      additionalInfo: string
      additionalInfoDelivery: string
      favoriteAlias?: string
    }) {
      const actualDeliveryAddress = this.provisionalAddress
      this.provisionalAddress = {
        ...(actualDeliveryAddress ?? {}),
        number: obj.number,
        complement: obj.additionalInfo || undefined,
        specialInstructions: obj.additionalInfoDelivery || undefined,
        latitude: actualDeliveryAddress?.latitude || 0,
        longitude: actualDeliveryAddress?.longitude || 0,
        alias: obj.favoriteAlias,
      }
    },

    addAddressDLV() {
      if (this.provisionalAddress && this.selectedRestaurant) {
        this.selectedRestaurant.addressDLV = this.provisionalAddress
      }
    },
    setDLVModalState(state: DLVModalState | undefined) {
      this.modalDLVState = state
    },
    openModal() {
      if (this.candidateArea) {
        this[`modal${this.candidateArea}`] = true
      }
    },
    openChangeRestaurantModal() {
      this.modalChangeRestaurant = true
    },
    closeModal() {
      this.modalDLV = false
      this.modalMOP = false
      // reset identifier producto
      this.productIdentifierAfterRestaurantSelected = undefined
    },
    async setArea({
      area,
      updateGoogleMaps,
    }: {
      area: RestaurantAreaType
      updateGoogleMaps?: boolean
    }) {
      this.candidateArea = area
      if (updateGoogleMaps) {
        const { key, country } = useGoogleMapsKey()
        try {
          await loadGoogleAPI({ apiKey: key, region: country })
          useGoogleSessionToken()
        } catch (error: any) {
          // eslint-disable-next-line no-console
          console.error('restaurantStore.setArea', error.message)
        }
      }
    },
    setGoogleMapsSessionToken(token: google.maps.places.AutocompleteSessionToken | undefined) {
      this.googleMapsSessionToken = token
    },
    closeChangeRestaurantModal() {
      this.modalChangeRestaurant = false
      this.modalDLVState = undefined
      this.closeModal()
    },
    async autoSelectRestaurant(
      code: string,
      area: RestaurantAreaType = RestaurantAreas.MOP
    ): Promise<boolean> {
      try {
        const appStore = useAppStore()
        const { trackSelectRestaurantAutomatic } = useGTM()
        await this.getRestaurantByCode({
          code,
          area,
        })
        if (!this.candidateArea || this.candidateArea !== area) this.candidateArea = area
        this.setRestaurant(this.restaurant!)
        appStore.clearCart()
        const { id } = this.restaurant!
        await appStore.loadCategories(id, area)
        trackSelectRestaurantAutomatic()
        return true
      } catch (error: any) {
        // eslint-disable-next-line no-console
        console.log(
          '🚀 ~ file: restaurant.store.ts:207 ~ autoSelectRestaurant ~ error:',
          error?.response || error
        )
        return false
      }
    },
    setRestaurantOpenState(state: OpenServicesState) {
      this.restaurantOpenState = state
    },
    setInitialDeliveryAddressDistance(deliveryAddressDistance: number) {
      this.selectedRestaurant = {
        ...this.selectedRestaurant,
        deliveryAddressDistance,
      }
    },
    async setDeliveryAddressDistance() {
      const currentCoordinates = this.getDeliveryCoords
      const restaurantCoordinates = this.getRestaurantCoords
      const deliveryAddressDistance = await getRouteDistance({
        currentCoordinates,
        restaurantCoordinates,
      })

      if (!deliveryAddressDistance) return

      if (this.selectedRestaurant?.addressDLV) {
        this.setAddress({
          ...this.selectedRestaurant?.addressDLV,
          distance: deliveryAddressDistance,
        })
        this.addAddressDLV()
      }

      this.selectedRestaurant = {
        ...this.selectedRestaurant,
        deliveryAddressDistance,
      }
    },
    setAnonymSelectedAddress() {
      if (this.selectedRestaurant?.addressDLV?.favoriteId) {
        this.selectedRestaurant.addressDLV = {
          ...this.selectedRestaurant.addressDLV,
          alias: undefined,
          favoriteId: undefined,
        }
      }
    },
    setRestaurantTips(options: TipsOptions) {
      this.restaurantTipsOptions = options
    },
  },
  getters: {
    isRestaurantSelected(): boolean {
      return !!this.selectedRestaurant?.restaurant?.code && !!this.restaurant
    },
    areaSelected(): RestaurantAreaType | undefined {
      return this.selectedRestaurant?.selectedArea
    },
    isAreaDLV(): boolean {
      return this.areaSelected === RestaurantAreas.DLV
    },
    tipsVisible(): boolean {
      return !!(this.isAreaDLV && this.restaurantTipsOptions?.enabled)
    },
    isGeoLocationActive(): boolean {
      return this.geoPermissionStatus === 'granted'
    },
    deliveryAddressDistance(): number | undefined {
      return this.selectedRestaurant?.deliveryAddressDistance
    },
    isCrew(): boolean | undefined {
      return this.selectedRestaurant?.isCrew
    },
    getServiceFeeName(): string | undefined {
      return this.selectedRestaurant?.serviceFee?.name
    },
    getServiceFeeAmount(): number | undefined {
      return this.selectedRestaurant?.serviceFee?.serviceFeeAmount
    },
    getSmallOrderFeeName(): string | undefined {
      return this.selectedRestaurant?.smallOrderFee?.name
    },
    getSmallOrderFeeAmount(): number | undefined {
      return this.selectedRestaurant?.smallOrderFee?.amount
    },
    getSmallOrderFeeMinAmount(): number | undefined {
      return this.selectedRestaurant?.smallOrderFee?.min
    },
    getDeliveryCoords(): Coords {
      return {
        lat: this.provisionalAddress?.latitude || 0,
        lng: this.provisionalAddress?.longitude || 0,
      }
    },
    getRestaurantCoords(): Coords {
      return {
        lat: this.restaurant?.coordinates.latitude || 0,
        lng: this.restaurant?.coordinates.longitude || 0,
      }
    },
    getSelectedArea(): RestaurantAreaType {
      if (!this.candidateArea && this.areaSelected) return this.areaSelected

      return this.candidateArea === RestaurantAreas.DLV ? RestaurantAreas.DLV : RestaurantAreas.MOP
    },
    getRestaurantTimezone(): string {
      return this.restaurant?.timezone ?? 'UTC'
    },
  },
  persist: {
    serializer: {
      // Parse persisted cookie paths selectedRestaurant
      deserialize: (value: string) => {
        const onError = () => {
          if (process.client) {
            // toast error
            const { $toast } = useNuxtApp()
            $toast.show({
              type: 'error',
              content: 'An error has ocurred while initializing app',
              duration: 5,
            })
            document.cookie = `restaurant=${encodeURIComponent(JSON.stringify({}))}; path=/; Secure`
          }
        }
        try {
          const json = JSON.parse(value)
          const key = 'selectedRestaurant'
          const keyValue = json[key]
          const validZod = selectedRestaurantSchema.safeParse(keyValue)
          if (!validZod.success) {
            onError()
            return undefined
          }
          return json
        } catch (error) {
          onError()
          return undefined
        }
      },
      serialize: JSON.stringify,
    },
    pick: ['selectedRestaurant'],
    storage: piniaPluginPersistedstate.cookies(),
  },
})
